import React, { useRef, useMemo, useState } from 'react';
import CategoryNavigation from '../../components/CategoryNavigation/CategoryNavigation';
import CustomSection from '../../components/CustomSection/CustomSection';
import DetailedSection from '../../components/DetailedSection/DetailedSection';
import FreeTrialGatedBlock from '../../components/GatedBlock/FreeTrialGatedBlock';
import GatedBlock from '../../components/GatedBlock/GatedBlock';
import { useGlobalContext } from '../../components/GlobalContextProvider/GlobalContextProvider';
import Loader from '../../components/Loader/Loader';
import SectionNavigation from '../../components/SectionNavigation/SectionNavigation';
import TableOfContents from '../../components/TableOfContents';
import { CUSTOM_SECTION_TYPE } from '../../consts/content-types';
import {
  FREE_USER_LEVEL,
  PAID_RESTRICTION_TYPE,
  PAID_USER_LEVEL,
  TRIAL_RESTRICTION_TYPE,
  TRIAL_USER_LEVEL,
} from '../../consts/restrictionTypes';
import useBreadcrumbsNav from '../../hooks/useBreadcrumbsNav';
import useDynamicContentFetch from '../../hooks/useDynamicContentFetch';
import useSendIsContentCroppedToGTM from '../../hooks/useSendIsContentCroppedToGTM';
import RequestUpdateButton from '../../shared-styled/blocks/RequestUpdateButton/RequestUpdateButton';
import {
  DetailedPageNav,
  Navigation,
  TOCWrapper,
  ArticleImage,
} from '../../shared-styled/blocks/detailedPageBlocks';
import { DetailedContentContainer } from '../../shared-styled/layout/DetailedContentContainer';
import { DetailPageTitle } from '../../shared-styled/typography/titles';
import styles from '../../styles/print-media.module.css';
import isDatePriorToNow from '../../utils/compareWithCurrentDate';
import { parseContent } from '../../utils/content';
import { IS_NEXT_EXPORT } from '../../utils/export';
import getImageAccessibilityProps from '../../utils/getImageAccessibilityProps';
import BasePageWrap from '../BasePage';

const domain = process.env.NEXT_PUBLIC_HOST_ENV
  ? `https://${process.env.NEXT_PUBLIC_HOST_ENV}`
  : 'https://dev.mp.quintagroup.com';

const ArmedForcesPage = (props) => {
  const { percentOfCoveredTitle, isLogged, user } = useGlobalContext();
  const [isLoading, setIsLoading] = useState();
  const [imageError, setImageError] = useState(false);

  const pageData = useDynamicContentFetch(props, setIsLoading);
  const { toc, contents } = useMemo(
    () => parseContent(pageData.section),
    [pageData.section],
  );
  const titleRef = useRef();
  useBreadcrumbsNav(titleRef);
  const isDetailedNavActive = pageData.relatedUrl.length > 1;

  const freeUserRestriction =
    user &&
    user.userLevel === FREE_USER_LEVEL &&
    (pageData.restrictionType === TRIAL_RESTRICTION_TYPE ||
      pageData.restrictionType === PAID_RESTRICTION_TYPE);
  const trialUserRestriction =
    user &&
    user.userLevel === TRIAL_USER_LEVEL &&
    pageData.restrictionType === PAID_RESTRICTION_TYPE;

  useSendIsContentCroppedToGTM(contents, pageData.restrictionType);

  return (
    <DetailedContentContainer>
      <Navigation
        $isDetailedNavActive={false}
        $opacity={percentOfCoveredTitle}
        $isVisibleOnPrint={true}
      >
        <CategoryNavigation className={styles.hideForPrint} />
        <DetailedPageNav ref={titleRef}>
          <DetailPageTitle>{pageData.parentItm.title}</DetailPageTitle>
          {isDetailedNavActive && (
            <SectionNavigation
              urlItems={pageData.relatedUrl}
              isModifiedSectionNavigationOnPrint={true}
            />
          )}
        </DetailedPageNav>
      </Navigation>

      <TOCWrapper>
        {pageData.photo && !imageError && (
          <ArticleImage
            onError={IS_NEXT_EXPORT ? () => setImageError(true) : undefined}
            src={domain + pageData.photo.path}
            {...getImageAccessibilityProps({ value: pageData.photo })}
          />
        )}
        <TableOfContents data={toc} className={styles.hideForPrint} />
      </TOCWrapper>
      <div>
        {contents?.map((el, index) =>
          el.type === CUSTOM_SECTION_TYPE ? (
            <CustomSection key={el.id} index={index} el={el} />
          ) : (
            <DetailedSection key={el.id} index={index} el={el} />
          ),
        )}
      </div>

      {isLoading && <Loader />}

      {isLogged &&
        (user.userLevel === PAID_USER_LEVEL ||
          (user.userLevel === TRIAL_USER_LEVEL && !trialUserRestriction)) && (
          <RequestUpdateButton
            className={styles.hideForPrint}
            isActive={
              !pageData.userNextRequestUpdate ||
              isDatePriorToNow(pageData.userNextRequestUpdate)
            }
            recordName={pageData.parentItm.title}
          />
        )}

      {!IS_NEXT_EXPORT && pageData.restricted && (
        <>
          {!isLogged && <GatedBlock isHigher={true} />}

          {isLogged &&
            process.env.NEXT_PUBLIC_LIMITED_FREE_TRIAL_ENABLED === 'True' &&
            (trialUserRestriction || freeUserRestriction) && (
              <FreeTrialGatedBlock
                isHigher={true}
                recordName={pageData.title}
              />
            )}
        </>
      )}
    </DetailedContentContainer>
  );
};

// props:
// {
//   section,
//   majorUpdateDate,
//   minorUpdateDate,
//   parentItm,
//   relatedUrl,
//   photo,
//   restricted,
// }

export default BasePageWrap(ArmedForcesPage);
